import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth, useIsOffline } from '@oneblink/apps-react'
import { OneBlinkAppsError } from '@oneblink/apps'
import { H3 } from '../components/TextComponents'
import styled from 'styled-components'

import {
  getAllReferenceDataMeta,
  getReferenceDataByType,
  getAllReferenceData,
  updateStore,
} from '../lib/service/reference-data'
import { getRequest } from 'lib/service/fetchService'
import config from 'config'
import LoadingSpinner from '../components/LoadingSpinner'
import Modal from 'components/Modal'
import { useSystemMaintenance } from './SystemMaintenanceProvider'

export interface TReferenceDataContext {
  referenceData?: ReferenceData[]
  fetchReferenceDataError?: OneBlinkAppsError
  isFetchingReferenceData?: boolean
}

const Offline = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`
Offline.displayName = 'Offline'

const ReferenceDataContext = React.createContext<TReferenceDataContext>({})

const EXPECTED_REFERENCEDATA_KEYS = [
  'certificateIssuingAOs',
  'facilities',
  'noPICReasons',
  'nominatedAOs',
  'requirementsAndMeasures',
]

console.log(
  `Expecting ${EXPECTED_REFERENCEDATA_KEYS.length} lots of reference data to come from pivotal`,
)

export const useReferenceData = function useReferenceData() {
  return React.useContext(ReferenceDataContext)
}

const fetchReferenceDataMeta = async (abortSignal: AbortSignal) => {
  const resp = await getRequest<ReferenceDataMeta[]>({
    url: `${config.OB_HOSTED_API_URL}/reference-data/types`,
    abortSignal,
  })
  if (!resp.body) {
    throw new OneBlinkAppsError(
      `No body returned from ${config.OB_HOSTED_API_URL}/reference-data/types`,
    )
  }
  return resp.body
}

const fetchReferenceDataByType = async (
  type: string,
  abortSignal: AbortSignal,
) => {
  const resp = await getRequest<ReferenceData>({
    url: `${config.OB_HOSTED_API_URL}/reference-data/types/${type}`,
    abortSignal,
  })
  if (!resp.body) {
    throw new OneBlinkAppsError(
      `No body returned from ${config.OB_HOSTED_API_URL}/reference-data/types/${type}`,
    )
  }
  return resp.body
}

export default function ReferenceDataProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const location = useLocation()
  const [state, setState] = React.useState<TReferenceDataContext>({})
  const {
    isFetchingReferenceData = false,
    referenceData,
    fetchReferenceDataError,
  } = state
  const [hasFetched, setHasFetched] = React.useState(false)
  const isOffline = useIsOffline()
  const { isLoggedIn } = useAuth()

  const isViewingForm = React.useMemo(
    () => /forms\/\d+$/i.test(location.pathname),
    [location.pathname],
  )

  React.useEffect(() => {
    setState((currentState) => ({
      referenceData: currentState.referenceData,
      isFetchingReferenceData: true,
    }))

    const abortController = new AbortController()

    const getLocalReferenceData = async () => {
      try {
        const newReferenceData = await getAllReferenceData()

        setState((currentState) => ({
          referenceData: Object.keys(newReferenceData).length
            ? newReferenceData
            : currentState.referenceData,
          isFetchingReferenceData: false,
        }))
        setHasFetched(true)
      } catch (error) {
        console.error(
          'Error attempting to retrieve local reference data',
          error,
        )
        if (!isViewingForm) {
          setState({ fetchReferenceDataError: error as OneBlinkAppsError })
        }
      }
    }

    const updateReferenceData = async () => {
      try {
        console.log('fetching reference data meta')
        const [deviceReferenceDataMeta, networkReferenceDataMeta] =
          await Promise.all([
            getAllReferenceDataMeta(),
            fetchReferenceDataMeta(abortController.signal),
          ])
        const promises = []
        const unchangedTypes: Array<ReferenceData> = []
        for (const networkMeta of networkReferenceDataMeta) {
          const deviceHash = deviceReferenceDataMeta[networkMeta.type]
          if (!deviceHash || deviceHash !== networkMeta.hash) {
            // either doesn't exist or hash doesn't match so fetch from network
            console.log(`fetching ${networkMeta.type} from api`)
            promises.push(
              fetchReferenceDataByType(
                networkMeta.type,
                abortController.signal,
              ),
            )
          } else {
            console.log(`getting ${networkMeta.type} from local storage`)
            const unchangedReferenceDataType = await getReferenceDataByType(
              networkMeta.type,
            )
            if (unchangedReferenceDataType) {
              unchangedTypes.push(unchangedReferenceDataType)
            }
          }
        }
        const networkReferenceData = await Promise.all(promises)
        const newReferenceData = [...unchangedTypes, ...networkReferenceData]
        await updateStore(newReferenceData)

        setState({ referenceData: newReferenceData })
        setHasFetched(true)
      } catch (error) {
        console.error('Error attempting to fetch reference data', error)
        if (!isViewingForm) {
          setState({ fetchReferenceDataError: error as OneBlinkAppsError })
        }
      }
    }

    if (!isOffline && isLoggedIn) {
      console.log('updating reference data')
      updateReferenceData()
    } else {
      console.log('getting reference data locally')
      getLocalReferenceData()
    }

    return () => {
      abortController.abort()
    }
  }, [isLoggedIn, isOffline, isViewingForm])

  const missingReferenceDataKeys = React.useMemo(() => {
    if (!Array.isArray(referenceData)) return EXPECTED_REFERENCEDATA_KEYS
    const currentTypes: string[] = referenceData.map((data) => data.type)
    const missingTypes = EXPECTED_REFERENCEDATA_KEYS.filter(
      (key) => !currentTypes.includes(key),
    )

    return missingTypes
  }, [referenceData])

  const { isInMaintenanceWindow } = useSystemMaintenance()

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined
    const tick = () => {
      if (isInMaintenanceWindow()) {
        window.document.body.classList.add('maintenance-mode')
      } else {
        window.document.body.classList.remove('maintenance-mode')
      }
      timeoutId = setTimeout(tick, 5000)
    }

    if (!timeoutId) {
      tick()
    }
    return () => {
      timeoutId && clearTimeout(timeoutId)
      if (isInMaintenanceWindow()) {
        window.document.body.classList.add('maintenance-mode')
      } else {
        window.document.body.classList.remove('maintenance-mode')
      }
    }
  }, [isInMaintenanceWindow])

  if (
    isOffline &&
    !isFetchingReferenceData &&
    (!referenceData || missingReferenceDataKeys.length)
  ) {
    return (
      <Offline>
        <i className="material-icons has-text-warning icon-x-large">wifi_off</i>
        <H3>Your device is offline</H3>
        <span>
          This application requires reference data only obtainable via an
          internet connection. Please connect to the internet to continue.
        </span>
      </Offline>
    )
  }

  if (
    isLoggedIn &&
    (fetchReferenceDataError ||
      (!fetchReferenceDataError &&
        referenceData &&
        missingReferenceDataKeys.length))
  ) {
    return (
      <>
        <H3>Could not update reference data</H3>
        <span>
          This application requires reference data which was unable to be
          retrieved: {missingReferenceDataKeys.join(', ')}. Please refresh and
          try again
        </span>
      </>
    )
  }

  return (
    <ReferenceDataContext.Provider value={state}>
      <Modal
        isOpen={
          isFetchingReferenceData && !isOffline && !referenceData && !hasFetched
        }
        title="Fetching Reference Data"
        cardClassName="has-text-centered"
        actions={null}
      >
        <LoadingSpinner />
      </Modal>
      {children}
    </ReferenceDataContext.Provider>
  )
}
